import React, { useState, useEffect } from 'react';
import { FiEdit, FiPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useUser } from './UserContext';

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const navigate = useNavigate();
  const { user } = useUser();

  const API_BASE_URL = 'https://arush1234sharma-querio-backend.hf.space/api/blog';

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/posts?page=${page}&limit=10`);
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch posts');
        }
        const data = await response.json();
        setPosts(data.posts);
        setTotalPosts(data.total);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [page]);

  const totalPages = Math.ceil(totalPosts / 10);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Blog</h1>
        {user && (
          <button
            onClick={() => navigate('/blog/create')}
            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center"
          >
            <FiPlus className="mr-2" /> New Post
          </button>
        )}
      </div>

      {posts.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No blog posts yet. Be the first to write one!</p>
        </div>
      ) : (
        <div className="space-y-8">
          {posts.map(post => (
            <div key={post.id} className="border-b border-gray-200 pb-8">
              <div className="flex justify-between items-start">
                <h2 className="text-2xl font-bold mb-2 hover:text-blue-500 cursor-pointer" onClick={() => navigate(`/blog/post/${post.id}`)}>
                  {post.title}
                </h2>
                {user && (
                  <button
                    onClick={() => navigate(`/blog/edit/${post.id}`)}
                    className="text-gray-500 hover:text-blue-500"
                    title="Edit post"
                  >
                    <FiEdit />
                  </button>
                )}
              </div>
              <div className="flex items-center text-sm text-gray-500 mb-4">
                <span>By {post.author}</span>
                <span className="mx-2">•</span>
                <span>{new Date(post.created_at).toLocaleDateString()}</span>
              </div>
              {post.cover_image && (
                <div className="mb-4">
                  <img 
                    src={post.cover_image} 
                    alt="Cover" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
              )}
              <div className="prose max-w-none mb-4">
                <ReactMarkdown>
                  {post.content.length > 500 ? post.content.substring(0, 500) + '...' : post.content}
                </ReactMarkdown>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags && post.tags.map(tag => (
                  <span
                    key={tag}
                    className="bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <button
                onClick={() => navigate(`/blog/post/${post.id}`)}
                className="text-blue-500 hover:underline"
              >
                Read more →
              </button>
            </div>
          ))}

          {totalPages > 1 && (
            <div className="flex justify-center space-x-2 mt-8">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogList;